<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'proposals'),
    screen: computed(() => 'create'),
  })
);

const currentStep = ref(0);
const searchQuery = ref('');

const asArray = value => (Array.isArray(value) ? value : []);
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => path.split('.').reduce((item, key) => item?.[key], value))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const imageUrlFrom = value => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return imageUrlFrom(value[0]);
  if (!value || typeof value !== 'object') return '';

  return imageUrlFrom(
    firstValue(value, ['url', 'src', 'image_url', 'imageUrl', 'preview_url'])
  );
};
const optionLabel = value => {
  const text = String(value || '').replaceAll('_', ' ');
  return text ? `${text[0].toLocaleUpperCase('pt-BR')}${text.slice(1)}` : '—';
};
const money = value => {
  if (value === '' || value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    return value.formatted || value.display || money(value.amount ?? value.value);
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(numeric);
};
const statusTone = value => {
  const status = String(value || '').toLocaleLowerCase('pt-BR');
  if (/dispon|ativo|livre/.test(status)) return 'bg-emerald-50 text-emerald-700';
  if (/negoci|reserv|pend/.test(status)) return 'bg-amber-50 text-amber-700';
  if (/inativ|cancel|recus/.test(status)) return 'bg-rose-50 text-rose-700';
  return 'bg-violet-50 text-[#5a24c7]';
};

const createOptions = computed(() => realData.data.value?.createOptions || {});
const properties = computed(() => asArray(createOptions.value.properties));
const contacts = computed(() => asArray(createOptions.value.contacts));
const listings = computed(() => asArray(createOptions.value.listings));
const transactions = computed(() => asArray(createOptions.value.transactions));
const fieldByKey = computed(() =>
  Object.fromEntries(model.fields.map(field => [field.key, field]))
);
const fieldFor = key => fieldByKey.value[key];
const propertyField = computed(() => fieldFor('imovel_id'));
const contactField = computed(() => fieldFor('contact_id'));
const valueFields = computed(() =>
  model.fields.filter(field => !['imovel_id', 'contact_id'].includes(field.key))
);

const locationFor = record =>
  firstValue(record, [
    'address.full_address',
    'endereco_completo',
    'endereco',
    'address.street',
    'address',
    'location',
  ]);
const titleFor = record =>
  firstValue(record, ['titulo', 'title', 'name', 'reference', 'code']);
const recordImage = record =>
  imageUrlFrom(
    firstValue(record, [
      'cover_url',
      'image_url',
      'thumbnail',
      'midias',
      'media',
      'images',
      'imovel.cover_url',
      'imovel.image_url',
      'property.cover_url',
      'property.image_url',
    ])
  );
const propertyIdFor = record =>
  String(
    firstValue(record, [
      'imovel_id',
      'property_id',
      'imovel.id',
      'property.id',
    ])
  );
const propertyDetails = record =>
  [
    ['Área', firstValue(record, ['area', 'area_total', 'area_privativa', 'metragem'])],
    ['Quartos', firstValue(record, ['bedrooms', 'quartos', 'dormitorios'])],
    ['Banheiros', firstValue(record, ['bathrooms', 'banheiros'])],
    ['Vagas', firstValue(record, ['parking_spaces', 'vagas', 'garages'])],
  ].filter(([, value]) => value !== '');
const amountFor = record =>
  money(
    firstValue(record, [
      'listing_price',
      'price_amount',
      'valor_venda',
      'sale_price',
      'value',
      'amount',
    ])
  );
const catalogItem = (record, source) => {
  const id = String(
    firstValue(record, [
      'id',
      'imovel_listing_id',
      'listing_id',
      'transaction_id',
      'reference',
    ])
  );

  return {
    amount: amountFor(record),
    details: propertyDetails(record),
    id,
    image: recordImage(record),
    kind: 'property',
    location: locationFor(record),
    propertyId: source === 'Imóvel' ? id : propertyIdFor(record),
    source,
    status: firstValue(record, [
      'status.label',
      'status',
      'availability_status',
      'primary_listing.status',
    ]),
    title: titleFor(record) || `${source} #${id}`,
  };
};
const catalog = computed(() => {
  const records = [
    ...properties.value.map(record => catalogItem(record, 'Imóvel')),
    ...listings.value.map(record => catalogItem(record, 'Listing')),
    ...transactions.value.map(record => catalogItem(record, 'Transação')),
  ].filter(item => item.id);
  const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');

  if (!query) return records;

  return records.filter(item =>
    `${item.title} ${item.location} ${item.status} ${item.source}`
      .toLocaleLowerCase('pt-BR')
      .includes(query)
  );
});
const selectedProperty = computed(() =>
  catalog.value.find(
    item => String(item.propertyId) === String(model.form.imovel_id)
  )
);
const selectedContact = computed(() =>
  contacts.value.find(contact => String(contact.id) === String(model.form.contact_id))
);
const requiredValueFieldsComplete = computed(() =>
  valueFields.value
    .filter(field => field.required)
    .every(field => String(model.form[field.key] || '').trim())
);
const steps = computed(() => [
  {
    complete: Boolean(model.form.imovel_id),
    label: 'Imóvel',
    subtitle: 'Selecionar',
  },
  {
    complete: Boolean(model.form.contact_id),
    label: 'Interessado',
    subtitle: 'Dados do cliente',
  },
  {
    complete: requiredValueFieldsComplete.value,
    label: 'Valores',
    subtitle: 'Proposta e condições',
  },
  {
    complete: Boolean(model.notice),
    label: 'Revisão',
    subtitle: 'Enviar proposta',
  },
]);
const summaryRows = computed(() => [
  {
    label: 'Imóvel',
    value:
      selectedProperty.value?.title ||
      (model.form.imovel_id ? `Imóvel #${model.form.imovel_id}` : 'Selecione um imóvel'),
  },
  {
    label: 'Interessado',
    value:
      selectedContact.value?.name ||
      (model.form.contact_id ? `Contato #${model.form.contact_id}` : 'Aguardando'),
  },
  {
    label: 'Valores e condições',
    value: requiredValueFieldsComplete.value ? 'Preenchidos' : 'Aguardando',
  },
  {
    label: 'Revisão e envio',
    value: model.notice ? 'Concluído' : 'Aguardando',
  },
  {
    label: 'Previsão de conclusão',
    value: '—',
  },
]);
const emptySlots = (kind, count, label) =>
  Array.from({ length: count }, (_, index) => ({
    id: `${kind}-${index}`,
    kind,
    label,
  }));
const paddedSlots = (items, kind, label) => [
  ...items.slice(0, 6),
  ...emptySlots(kind, Math.max(0, 6 - items.length), label),
];
const propertySlots = computed(() => {
  const available = catalog.value;
  if (available.length) return paddedSlots(available, 'unavailable', 'Opção indisponível');

  return [
    ...(propertyField.value
      ? [{ id: 'manual-property', kind: 'manual-property', label: propertyField.value.label }]
      : []),
    ...emptySlots(
      'unavailable',
      propertyField.value ? 5 : 6,
      'Imóvel indisponível'
    ),
  ];
});
const contactSlots = computed(() => {
  const available = contacts.value.map(contact => ({
    id: String(contact.id),
    kind: 'contact',
    name: contact.name || `Contato #${contact.id}`,
    thumbnail: contact.thumbnail,
    value: contact.phone_number || contact.email || '',
  }));
  if (available.length) return paddedSlots(available, 'unavailable', 'Contato indisponível');

  return [
    ...(contactField.value
      ? [{ id: 'manual-contact', kind: 'manual-contact', label: contactField.value.label }]
      : []),
    ...emptySlots(
      'unavailable',
      contactField.value ? 5 : 6,
      'Contato indisponível'
    ),
  ];
});
const fieldSlots = computed(() =>
  paddedSlots(
    valueFields.value.map(field => ({ ...field, id: field.key, kind: 'field' })),
    'unavailable',
    'Campo indisponível'
  )
);
const reviewSlots = computed(() =>
  paddedSlots(
    [
      ...summaryRows.value,
      ...valueFields.value.map(field => ({
        label: field.label,
        value:
          field.key === 'offer_amount'
            ? money(model.form[field.key])
            : optionLabel(model.form[field.key]),
      })),
    ].map((item, index) => ({ ...item, id: `review-${index}`, kind: 'review' })),
    'unavailable',
    'Dado indisponível'
  )
);
const stageSlots = computed(() => {
  if (currentStep.value === 0) return propertySlots.value;
  if (currentStep.value === 1) return contactSlots.value;
  if (currentStep.value === 2) return fieldSlots.value;

  return reviewSlots.value;
});
const canAdvance = computed(() => {
  if (currentStep.value === 0) return Boolean(model.form.imovel_id);
  if (currentStep.value === 1) return Boolean(model.form.contact_id);
  if (currentStep.value === 2) return requiredValueFieldsComplete.value;

  return model.canCreate && requiredValueFieldsComplete.value;
});
const continueLabel = computed(() =>
  currentStep.value === 3 ? 'Criar proposta' : 'Continuar'
);
const selectProperty = item => {
  if (!item.propertyId) return;

  model.form.imovel_id = item.propertyId;
  currentStep.value = 1;
};
const selectContact = item => {
  model.form.contact_id = item.id;
  currentStep.value = 2;
};
const advance = async () => {
  if (!canAdvance.value) return;
  if (currentStep.value < 3) {
    currentStep.value += 1;
    return;
  }

  const response = await model.create();
  if (response) emit('navigate', 'detail');
};
</script>

<template>
  <main
    data-imudei-visual-id="proposal-create-root"
    class="-ml-[54px] w-[calc(100%+54px)] pb-[78px] pl-10 pr-7 pt-4 text-[#101958]"
  >
    <header class="flex h-[91px] items-start gap-6 pl-[75px] pr-[870px] pt-2">
      <h1 class="whitespace-nowrap text-[28px] font-bold tracking-[-0.035em]">Nova proposta</h1>
      <span class="mt-2 flex whitespace-nowrap items-center gap-2 text-[9px] text-[#526087]">
        <span :class="model.notice ? 'i-lucide-circle-check text-emerald-600' : 'i-lucide-cloud-off text-[#7d87a4]'" class="size-3.5" />
        {{ model.notice || 'Dados ainda não enviados à API' }}
      </span>
    </header>

    <div class="grid grid-cols-[minmax(0,1fr)_388px] items-start gap-4">
      <section class="relative -left-[14px] w-[calc(100%+14px)] min-w-0 space-y-4">
        <section
          data-imudei-visual-id="proposal-create-progress"
          class="flex h-[66px] items-center rounded-2xl border border-[#e9e5f1] bg-white pl-[56px] pr-7 shadow-[0_3px_15px_rgba(51,31,93,0.035)]"
        >
          <span v-for="(stage, index) in steps" :key="stage.label" class="relative flex flex-1 items-center gap-3">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-full text-[13px] font-bold"
              :class="index === currentStep ? 'bg-gradient-to-br from-[#541ed0] to-[#862fee] text-white shadow-[0_5px_16px_rgba(91,32,202,0.25)]' : stage.complete ? 'border border-[#d7cafc] bg-[#f1ecff] text-[#5b20d2]' : 'border border-[#dad5e6] bg-[#faf9fd] text-[#25316c]'"
            >{{ index + 1 }}</span>
            <span class="min-w-0">
              <strong class="block truncate text-[10px]" :class="index === currentStep ? 'text-[#5b20d2]' : ''">{{ stage.label }}</strong>
              <small class="mt-0.5 block truncate text-[8px] text-[#69759a]">{{ stage.subtitle }}</small>
            </span>
            <span v-if="index < 3" class="relative mx-5 h-px flex-1 bg-[#ded9e8]">
              <span class="absolute left-1/2 top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-[#d2c9e5] bg-white" />
            </span>
            <span v-if="index > currentStep && !stage.complete" class="i-lucide-lock-keyhole mr-2 size-3 text-[#8a94b0]" />
          </span>
        </section>

        <section
          data-imudei-visual-id="proposal-create-catalog"
          class="relative h-[670px] overflow-hidden rounded-2xl border border-[#e9e5f1] bg-white p-5 pl-[42px] shadow-[0_3px_15px_rgba(51,31,93,0.035)]"
        >
          <nav class="flex h-10 items-center gap-5 border-b border-[#eeeaf4]">
            <button
              class="flex h-10 items-center gap-2 border-b-2 px-3 text-[10px] font-bold"
              :class="model.form.business_context === 'purchase' ? 'border-[#5b20d2] text-[#5b20d2]' : 'border-transparent text-[#68759b]'"
              type="button"
              @click="model.form.business_context = 'purchase'"
            >
              <span class="i-lucide-building-2 size-4" />Venda
            </button>
            <button
              class="flex h-10 items-center gap-2 border-b-2 px-3 text-[10px]"
              :class="model.form.business_context === 'rental' ? 'border-[#5b20d2] font-bold text-[#5b20d2]' : 'border-transparent text-[#68759b]'"
              type="button"
              @click="model.form.business_context = 'rental'"
            >
              <span class="i-lucide-bed-double size-4" />Aluguel
            </button>
            <span class="ml-auto grid size-8 place-items-center rounded-full bg-[#f3edff] text-[#7663a3]" title="Recompensas indisponíveis pela API.">
              <span class="i-lucide-star-off size-4" />
            </span>
          </nav>

          <header class="mt-2 grid grid-cols-[minmax(0,1fr)_100px_182px] items-center gap-3">
            <label class="flex h-11 items-center gap-3 rounded-xl border border-[#e3deeb] px-4 text-[#8a94b0]">
              <span class="i-lucide-search size-4" />
              <input
                v-model="searchQuery"
                class="min-w-0 flex-1 !border-0 !bg-transparent !p-0 text-[9px] !text-[#34406d] !shadow-none outline-none placeholder:text-[#8a94b0] focus:!border-0 focus:outline-none focus:ring-0"
                placeholder="Buscar imóvel por código, rua, bairro ou empreendimento"
                type="search"
              />
            </label>
            <span class="flex h-11 items-center justify-center gap-2 rounded-xl border border-[#e3deeb] text-[9px] font-bold text-[#68759b]">
              <span class="i-lucide-sliders-horizontal size-4 text-[#7663a3]" />Filtros
              <span class="rounded-full bg-[#f1ebff] px-1.5 py-0.5 text-[7px] text-[#7663a3]">—</span>
            </span>
            <span
              class="flex h-11 items-center justify-center gap-2 rounded-xl border border-dashed border-[#bfa7fb] text-[9px] font-bold text-[#7a67a1]"
              title="A API atual não disponibiliza inclusão de imóvel externo neste fluxo."
            >
              <span class="i-lucide-plus size-4" />Adicionar imóvel externo
            </span>
          </header>

          <div class="mt-1 grid grid-cols-3 gap-4">
            <template v-for="item in stageSlots" :key="item.id">
              <button
                v-if="item.kind === 'property'"
                class="h-[248px] overflow-hidden rounded-xl border bg-white text-left transition focus:outline-none focus:ring-2 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
                :class="String(item.propertyId) === String(model.form.imovel_id) ? 'border-[#6b2ee6] ring-2 ring-violet-100' : 'border-[#e7e2ed]'"
                :disabled="!item.propertyId"
                type="button"
                @click="selectProperty(item)"
              >
                <span class="relative block h-[126px] overflow-hidden" :class="item.image ? 'bg-gradient-to-br from-[#f5f1ff] via-[#f0edfb] to-[#eaf2ff]' : 'bg-gradient-to-br from-[#d8d1ce] via-[#c6d5df] to-[#d5c5b8]'">
                  <img v-if="item.image" :alt="item.title" :src="item.image" class="h-full w-full object-cover" />
                  <span v-else class="flex h-full flex-col justify-between p-3 text-[#433b65]">
                    <span class="flex items-center gap-1.5 text-[7px] font-bold"><span class="i-lucide-image-off size-3.5" />Mídia indisponível</span>
                    <span class="grid flex-1 place-items-center"><span class="grid size-11 place-items-center rounded-xl border border-white/70 bg-white/45 text-[#5c4b87]"><span class="i-lucide-building-2 size-6" /></span></span>
                    <small class="text-[7px] font-medium text-[#433b65]">A API não retornou foto</small>
                  </span>
                  <span v-if="item.status" class="absolute left-2 top-2 rounded-md px-2 py-1 text-[7px] font-bold" :class="statusTone(item.status)">{{ optionLabel(item.status) }}</span>
                  <span class="absolute right-2 top-2 rounded-md bg-white/90 px-2 py-1 text-[7px] font-bold text-[#5b20d2]">{{ item.source }}</span>
                  <span v-if="String(item.propertyId) === String(model.form.imovel_id)" class="absolute bottom-3 right-3 grid size-6 place-items-center rounded-full bg-white text-[#5b20d2]"><span class="i-lucide-check size-3.5" /></span>
                </span>
                <span class="block px-3 py-2">
                  <strong class="block truncate text-[10px]">{{ item.title }}</strong>
                  <p class="mt-0.5 truncate text-[8px] text-[#55618a]">{{ item.location || 'Localização não retornada pela API' }}</p>
                  <div v-if="item.details.length" class="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[7px] text-[#59658e]">
                    <span v-for="detail in item.details" :key="detail[0]">{{ detail[0] }}: {{ detail[1] }}</span>
                  </div>
                  <b v-if="item.amount !== '—'" class="mt-1 block text-[11px] text-[#4d18d2]">{{ item.amount }}</b>
                  <small v-else-if="!item.propertyId" class="mt-1 block text-[7px] text-amber-700">Sem imóvel vinculado</small>
                </span>
              </button>

              <button
                v-else-if="item.kind === 'contact'"
                class="flex h-[248px] flex-col items-center justify-center rounded-xl border border-[#e7e2ed] bg-white p-5 text-center transition focus:outline-none focus:ring-2 focus:ring-violet-100"
                :class="String(item.id) === String(model.form.contact_id) ? 'border-[#6b2ee6] ring-2 ring-violet-100' : ''"
                type="button"
                @click="selectContact(item)"
              >
                <img v-if="item.thumbnail" :alt="item.name" :src="item.thumbnail" class="size-16 rounded-full object-cover ring-2 ring-[#c7b4fb] ring-offset-4" />
                <span v-else class="grid size-16 place-items-center rounded-full bg-[#f0e9ff] text-[15px] font-bold text-[#5922c8] ring-2 ring-[#c7b4fb] ring-offset-4">{{ initials(item.name) }}</span>
                <strong class="mt-5 block max-w-full truncate text-[11px]">{{ item.name }}</strong>
                <small class="mt-2 block max-w-full truncate text-[8px] text-[#69759a]">{{ item.value || 'Canal não retornado pela API' }}</small>
                <span class="mt-4 flex items-center gap-1 text-[8px] font-semibold" :class="String(item.id) === String(model.form.contact_id) ? 'text-[#5b20d2]' : 'text-[#7a84a2]'">
                  <span :class="String(item.id) === String(model.form.contact_id) ? 'i-lucide-circle-check' : 'i-lucide-user-round'" class="size-3.5" />{{ String(item.id) === String(model.form.contact_id) ? 'Selecionado' : 'Contato real' }}
                </span>
              </button>

              <label v-else-if="item.kind === 'field'" class="flex h-[248px] flex-col rounded-xl border border-[#e7e2ed] bg-white p-4 text-left">
                <span class="grid size-9 place-items-center rounded-full bg-[#f1ebff] text-[#5a24c7]">
                  <span :class="item.type === 'textarea' ? 'i-lucide-notebook-pen' : item.type === 'select' ? 'i-lucide-list-filter' : 'i-lucide-banknote'" class="size-4" />
                </span>
                <strong class="mt-4 text-[10px]">{{ item.label }}<sup v-if="item.required" class="ml-0.5 text-rose-500">*</sup></strong>
                <textarea v-if="item.type === 'textarea'" v-model="model.form[item.key]" class="mt-3 min-h-[78px] w-full resize-none rounded-lg border border-[#e3deeb] px-3 py-2 text-[8px] font-normal leading-4 outline-none focus:border-[#8a5cf0]" :required="item.required" />
                <select v-else-if="item.type === 'select'" v-model="model.form[item.key]" class="mt-3 h-10 w-full rounded-lg border border-[#e3deeb] bg-white px-3 text-[8px] font-normal outline-none focus:border-[#8a5cf0]" :required="item.required">
                  <option value="">Selecione</option>
                  <option v-for="option in item.options || []" :key="option" :value="option">{{ optionLabel(option) }}</option>
                </select>
                <input v-else v-model="model.form[item.key]" class="mt-3 h-10 w-full rounded-lg border border-[#e3deeb] px-3 text-[8px] font-normal outline-none focus:border-[#8a5cf0]" :required="item.required" :type="item.type" />
                <small class="mt-auto text-[8px] text-[#707b9e]">Campo disponível para esta proposta</small>
              </label>

              <article v-else-if="item.kind === 'review'" class="flex h-[248px] flex-col rounded-xl border border-[#e7e2ed] bg-white p-4">
                <span class="grid size-9 place-items-center rounded-full bg-[#f1ebff] text-[#5a24c7]"><span class="i-lucide-clipboard-check size-4" /></span>
                <strong class="mt-4 text-[10px]">{{ item.label }}</strong>
                <p class="mt-3 break-words text-[9px] leading-4 text-[#657199]">{{ item.value }}</p>
                <small class="mt-auto text-[8px] text-[#7b84a0]">Valor atual do formulário</small>
              </article>

              <label v-else-if="item.kind === 'manual-property'" class="flex h-[248px] flex-col rounded-xl border border-dashed border-[#cbb8fb] bg-[#fdfbff] p-4">
                <span class="grid size-9 place-items-center rounded-full bg-[#f1ebff] text-[#5a24c7]"><span class="i-lucide-hash size-4" /></span>
                <strong class="mt-4 text-[10px]">{{ item.label }}</strong>
                <p class="mt-2 text-[8px] leading-4 text-[#707b9e]">Nenhuma opção foi retornada. Informe somente um ID que exista na operação.</p>
                <input v-model="model.form.imovel_id" class="mt-3 h-10 rounded-lg border border-[#e3deeb] px-3 text-[9px] outline-none focus:border-[#8a5cf0]" inputmode="numeric" type="number" />
              </label>

              <label v-else-if="item.kind === 'manual-contact'" class="flex h-[248px] flex-col rounded-xl border border-dashed border-[#cbb8fb] bg-[#fdfbff] p-4">
                <span class="grid size-9 place-items-center rounded-full bg-[#f1ebff] text-[#5a24c7]"><span class="i-lucide-hash size-4" /></span>
                <strong class="mt-4 text-[10px]">{{ item.label }}</strong>
                <p class="mt-2 text-[8px] leading-4 text-[#707b9e]">Nenhuma opção foi retornada. Informe somente um ID de contato real.</p>
                <input v-model="model.form.contact_id" class="mt-3 h-10 rounded-lg border border-[#e3deeb] px-3 text-[9px] outline-none focus:border-[#8a5cf0]" inputmode="numeric" type="number" />
              </label>

              <article v-else class="flex h-[248px] flex-col items-center justify-center rounded-xl border border-dashed border-[#ded9e8] bg-[#fcfbfe] p-5 text-center">
                <span class="grid size-12 place-items-center rounded-full bg-[#f2eff8] text-[#8a92aa]"><span class="i-lucide-circle-slash-2 size-5" /></span>
                <strong class="mt-4 text-[10px] text-[#4d587f]">{{ item.label }}</strong>
                <p class="mt-2 max-w-[180px] text-[8px] leading-4 text-[#7b84a0]">A API não disponibilizou dados para este slot.</p>
              </article>
            </template>
          </div>
          <span class="absolute bottom-1 left-1/2 flex h-8 -translate-x-1/2 items-center gap-2 rounded-lg border border-[#cbb8fb] px-7 text-[8px] font-semibold text-[#5b20d2]">
            Nenhum outro imóvel retornado pela API
            <span class="i-lucide-chevron-down size-3" />
          </span>
        </section>

        <section class="relative top-1 grid grid-cols-4 gap-4">
          <article v-for="stage in steps" :key="`reward-${stage.label}`" class="relative flex h-[64px] items-center gap-3 rounded-2xl border border-[#e5e1ec] bg-white px-4 text-[#45517b]">
            <span class="grid size-10 shrink-0 place-items-center rounded-full bg-[#f2eff8] text-[#8a94b0]"><span class="i-lucide-lock-keyhole size-5" /></span>
            <span class="min-w-0"><strong class="block truncate text-[9px]">{{ stage.label }}</strong><small class="mt-1 block text-[8px] text-[#7b84a0]">{{ stage.complete ? 'Dados preenchidos' : 'Recompensa indisponível' }}</small></span>
          </article>
        </section>
      </section>

      <aside data-imudei-visual-id="proposal-create-sidebar" class="space-y-4">
        <article class="h-[93px] overflow-hidden rounded-2xl border border-[#d9c8ff] bg-gradient-to-r from-white to-[#fbf4ff] p-4 shadow-[0_4px_18px_rgba(80,41,153,0.06)]">
          <div class="flex items-center gap-3">
            <span class="grid size-12 place-items-center rounded-full bg-[#f0eaff] text-[#6c42c9] shadow-[0_5px_18px_rgba(110,72,226,0.16)]"><span class="i-lucide-star-off size-6" /></span>
            <span><strong class="text-[12px]">Recompensas indisponíveis</strong><p class="mt-1 text-[8px]">A API não retornou pontos, ranking ou missões para esta proposta.</p></span>
            <b class="ml-auto rounded-xl bg-[#f1ebff] px-3 py-2 text-[18px] text-[#8a94b0]">—</b>
          </div>
          <div class="mt-3 flex items-center gap-1 pl-[60px]"><span v-for="index in 7" :key="index" class="h-1.5 flex-1 rounded-full" :class="index <= currentStep + 1 ? 'bg-[#8363cb]' : 'bg-[#e8e3ef]'" /></div>
        </article>

        <article class="h-[217px] overflow-hidden rounded-2xl border border-[#e8e3ef] bg-white p-4">
          <header class="flex items-center justify-between"><h2 class="text-[11px] font-bold">Resumo da proposta</h2><span class="rounded-full bg-[#6d32de] px-2 py-1 text-[7px] font-bold text-white">Novo</span></header>
          <dl class="mt-3 divide-y divide-[#eeeaf4] text-[8px]"><div v-for="item in summaryRows" :key="item.label" class="flex items-center justify-between gap-4 py-2.5"><dt class="font-semibold">{{ item.label }}</dt><dd class="max-w-[58%] truncate text-right text-[#67739a]">{{ item.value }}</dd></div></dl>
        </article>

        <article class="h-[143px] overflow-hidden rounded-2xl border border-[#e8e3ef] bg-white p-4">
          <header class="flex justify-between"><h2 class="text-[11px] font-bold">Participantes</h2><span class="text-[8px] font-bold text-[#7b84a0]">Sem endpoint</span></header>
          <div class="mt-3 flex justify-between">
            <span v-if="selectedContact" class="text-center"><img v-if="selectedContact.thumbnail" :alt="selectedContact.name" :src="selectedContact.thumbnail" class="mx-auto size-10 rounded-full object-cover ring-2 ring-[#5b20d2] ring-offset-2" /><span v-else class="mx-auto grid size-10 place-items-center rounded-full bg-[#f0e9ff] text-[8px] font-bold text-[#5b20d2] ring-2 ring-[#5b20d2] ring-offset-2">{{ initials(selectedContact.name) }}</span><strong class="mt-2 block max-w-[58px] truncate text-[7px]">{{ selectedContact.name }}</strong><small class="block text-[6px] text-[#69759a]">Interessado</small></span>
            <span v-for="index in selectedContact ? 3 : 4" :key="`participant-${index}`" class="text-center"><span class="mx-auto grid size-10 place-items-center rounded-full border border-dashed border-[#d9d3e6] bg-[#faf9fd] text-[#929ab0]"><span class="i-lucide-user-round-x size-4" /></span><strong class="mt-2 block text-[7px] text-[#7d86a2]">Indisponível</strong><small class="block text-[6px] text-[#a0a7ba]">Sem dados</small></span>
            <span class="text-center"><span class="mx-auto grid size-10 place-items-center rounded-full border border-dashed border-[#d9d3e6] bg-[#faf9fd] text-[#929ab0]"><span class="i-lucide-plus size-4" /></span><strong class="mt-2 block text-[7px] text-[#7d86a2]">Convidar</strong></span>
          </div>
        </article>

        <article class="h-[190px] overflow-hidden rounded-2xl border border-[#e8e3ef] bg-white p-4">
          <header class="flex justify-between"><h2 class="text-[11px] font-bold">Atividades recentes</h2><span class="text-[8px] font-bold text-[#7b84a0]">Sem endpoint</span></header>
          <div v-for="index in 3" :key="`activity-${index}`" class="mt-3 flex items-center gap-3"><span class="grid size-8 place-items-center rounded-full bg-[#f2eff8] text-[#9198ae]"><span class="i-lucide-clock-3 size-4" /></span><span><strong class="text-[8px] text-[#657099]">Atividade indisponível</strong><span class="ml-1 text-[8px] text-[#7f88a2]">para proposta não criada</span><small class="mt-1 block text-[7px] text-[#9aa1b4]">Aguardando retorno da API</small></span><span class="i-lucide-circle-slash-2 ml-auto size-4 text-[#a1a7ba]" /></div>
        </article>

        <article class="h-[132px] overflow-hidden rounded-2xl border border-[#e8e3ef] bg-white p-4">
          <header class="flex justify-between"><h2 class="text-[11px] font-bold">Conquistas</h2><span class="text-[8px] font-bold text-[#7b84a0]">Sem endpoint</span></header>
          <div class="mt-4 flex items-center justify-between"><span v-for="index in 5" :key="`achievement-${index}`" class="grid size-11 place-items-center rounded-full border border-[#e3deea] bg-[#faf9fd] text-[#9aa1b4]"><span class="i-lucide-lock-keyhole size-5" /></span></div>
        </article>
      </aside>
    </div>

    <p v-if="model.unavailableMessage" class="fixed bottom-[70px] right-7 z-50 max-w-sm rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-[9px] leading-4 text-[#6540a8]">{{ model.unavailableMessage }}</p>
    <p v-if="model.localError || error" class="fixed bottom-[70px] right-7 z-50 max-w-sm rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[9px] leading-4 text-rose-600">{{ model.localError || error }}</p>

    <footer data-imudei-visual-id="proposal-create-footer" class="fixed bottom-[11px] left-5 right-5 z-40 flex h-[54px] items-center rounded-xl border border-[#e7e2ed] bg-white/95 px-5 shadow-[0_-6px_22px_rgba(47,29,87,0.04)] backdrop-blur">
      <button class="relative -left-10 h-9 rounded-lg border border-[#a989ef] px-9 text-[9px] font-bold text-[#5b20d2]" type="button" @click="emit('navigate', 'dashboard')">Cancelar</button>
      <span class="mx-auto flex items-center gap-2 text-[8px] text-[#647096]"><span :class="model.notice ? 'i-lucide-circle-check text-emerald-600' : 'i-lucide-cloud-off text-[#7d87a4]'" class="size-3.5" />{{ model.notice || 'Nenhum dado foi enviado à API' }}</span>
      <button class="flex h-9 items-center gap-4 rounded-lg bg-gradient-to-r from-[#6a2ee5] via-[#8145ef] to-[#e54cb3] px-[85px] text-[10px] font-bold text-white shadow-[0_6px_18px_rgba(112,45,218,0.22)] disabled:cursor-not-allowed disabled:opacity-50" :disabled="saving || !canAdvance || (currentStep === 3 && !model.canCreate)" type="button" @click="advance">{{ saving ? 'Criando…' : continueLabel }}<span :class="currentStep === 3 ? 'i-lucide-save' : 'i-lucide-arrow-right'" class="size-4" /></button>
    </footer>
  </main>
</template>
